import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "./skeleton";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Optional section heading; consecutive options sharing a group render under one header. */
  group?: string;
  /** Free-form tags rendered as small badges (e.g. voice gender/accent). Values shown, keys ignored. */
  meta?: Record<string, string>;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Render labels in the mono face (technical values, ids). */
  mono?: boolean;
  /** Show a loading affordance in the open list (dynamic options still resolving). */
  isLoading?: boolean;
  /** Show an error affordance in the open list. */
  error?: boolean;
  disabled?: boolean;
  id?: string;
}

/**
 * Accessible, searchable single-select dropdown.
 *
 * Trigger button (selected label) + popover with a substring-filter search box
 * and a scrollable listbox. Keyboard: ArrowUp/Down to move, Enter to select,
 * Escape to close. Closes on outside click. Supports {value,label,group?,meta?}.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches",
  mono,
  isLoading,
  error,
  disabled,
  id,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const baseId = useId();
  const listboxId = `${id ?? baseId}-listbox`;
  const optionId = (i: number) => `${id ?? baseId}-opt-${i}`;

  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        o.group?.toLowerCase().includes(q) ||
        (o.meta && Object.values(o.meta).some((v) => v.toLowerCase().includes(q)))
    );
  }, [options, query]);

  // When opening, focus search and point the active row at the current selection.
  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the active row clamped + scrolled into view as the filter narrows.
  useEffect(() => {
    if (!open) return;
    setActiveIndex((i) => Math.min(i, Math.max(0, filtered.length - 1)));
  }, [filtered.length, open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  function close(focusTrigger = false) {
    setOpen(false);
    setQuery("");
    if (focusTrigger) triggerRef.current?.focus();
  }

  function commit(opt: ComboboxOption | undefined) {
    if (!opt) return;
    onChange(opt.value);
    close(true);
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length) setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length) setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    } else if (e.key === "Tab") {
      close();
    }
  }

  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setOpen(true);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        className={cn(
          "flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-studio-border bg-studio-card px-3.5 text-base outline-none transition-[border-color,box-shadow] hover:border-studio-border-light focus:border-studio-accent/60 focus:shadow-glow-sm disabled:cursor-not-allowed disabled:opacity-50 sm:h-10 sm:text-sm",
          open && "border-studio-accent/60 shadow-glow-sm"
        )}
      >
        <span className={cn("flex min-w-0 items-center gap-2 truncate", !selected && "text-studio-faint")}>
          <span className={cn("truncate", mono && "font-mono")}>{selected ? selected.label : placeholder}</span>
          {selected?.meta && <MetaBadges meta={selected.meta} />}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-studio-faint" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1.5 overflow-hidden rounded-xl border border-studio-border-light bg-studio-panel shadow-panel animate-fade-in">
          <div className="flex items-center gap-2 border-b border-studio-border px-3">
            <Search className="h-3.5 w-3.5 shrink-0 text-studio-faint" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={filtered.length ? optionId(activeIndex) : undefined}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder={searchPlaceholder}
              className="h-10 w-full bg-transparent text-sm text-studio-ink outline-none placeholder:text-studio-faint"
            />
          </div>

          <ul ref={listRef} role="listbox" id={listboxId} className="max-h-[280px] overflow-y-auto p-1">
            {isLoading && filtered.length === 0 ? (
              <li role="presentation" className="space-y-1.5 p-1.5">
                {Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </li>
            ) : error && filtered.length === 0 ? (
              <li
                role="presentation"
                className="flex items-center gap-2 px-3 py-6 text-xs text-studio-danger"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                Failed to load options
              </li>
            ) : filtered.length === 0 ? (
              <li role="presentation" className="px-3 py-6 text-center text-xs text-studio-faint">
                {emptyText}
              </li>
            ) : (
              filtered.map((opt, i) => {
                const showGroup = Boolean(opt.group) && opt.group !== filtered[i - 1]?.group;
                const isActive = i === activeIndex;
                const isSelected = opt.value === value;
                return (
                  <Fragment key={opt.value}>
                    {showGroup && (
                      <li
                        role="presentation"
                        aria-hidden="true"
                        className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-studio-faint"
                      >
                        {opt.group}
                      </li>
                    )}
                    <li
                      id={optionId(i)}
                      role="option"
                      aria-selected={isSelected}
                      data-index={i}
                      onClick={() => commit(opt)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm transition-colors",
                        isActive ? "bg-studio-card-hover text-studio-ink" : "text-studio-muted"
                      )}
                    >
                      <Check
                        className={cn(
                          "h-3.5 w-3.5 shrink-0 text-studio-accent",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className={cn("min-w-0 flex-1 truncate", mono && "font-mono")}>{opt.label}</span>
                      {opt.meta && <MetaBadges meta={opt.meta} />}
                    </li>
                  </Fragment>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Render meta values (e.g. gender/accent) as compact rounded badges. */
function MetaBadges({ meta }: { meta: Record<string, string> }) {
  const values = Object.values(meta).filter(Boolean);
  if (values.length === 0) return null;
  return (
    <span className="flex shrink-0 items-center gap-1">
      {values.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="rounded border border-studio-border bg-studio-card px-1.5 py-0.5 text-[10px] font-medium capitalize text-studio-faint"
        >
          {v}
        </span>
      ))}
    </span>
  );
}
